//Constants
import {
	ADC_ACTIONS,
	EXT_HUM,
	EXT_TEMP,
	HALF_SLEEPING_TYPES,
	SLEEPING_TYPES,
} from '../../../../constants';
//Helpers
import { real_id } from '../../../../helpers/interface_helpers';
//Services
import { localReq, postReq } from '../../../../services/http';
//Types
import type { GetCloudActionsRequest } from '../../../../types/requests/services/get_cloud_actions';

/**
 * action_format { index: number, enabled: boolean, urls: array, params?: {threshold_value : number, one_time: boolean} }
 */
/**get actions - CLOUD req */
const getCloudActions = async (id, channel, timeout = 5000) => {
	try {
		return await postReq(`/device/get_actions_urls`, { id, channel }, timeout);
	} catch (error) {
		return { isok: false };
	}
};

/**get actions LOCAL req */
const getLocalActions = async (device, localIp, data, timeout = 5000, qs = false) => {
	try {
		return await localReq(device, `${localIp}/settings/actions/`, 'post', data, timeout, qs);
	} catch (error) {
		return { isok: false };
	}
};

/**FOR every action we expect index prop (it is returned by local, but no from cloud, so add it) */
const addIndexToCloudActions = (_actions, index, removeActions) => {
	let actions;
	if (typeof _actions === 'object') {
		for (let [_n, _act] of Object.entries(_actions)) {
			if (typeof _act === 'object' && !removeActions.includes(_n)) {
				_act['index'] = index;
				actions = { ...actions, [_n]: _act };
			}
		}
		return actions;
	} else {
		return false;
	}
};

/** Make the single action looks like the above defined action_format*/
//normalize one action only
const normalizeLocalAction = (action, index = 0) => {
	//{...action[0], params: {[name.includes('under')? 'under_power_url_threshold' : 'over_power_url_threshold'] : action[0][[name.includes('under')? 'under_power_url_threshold' : 'over_power_url_threshold']]}}}
	let _action = {},
		_params = {};

	for (let [_key, _value] of Object.entries(action[index])) {
		if (['enabled', 'urls', 'index'].includes(_key)) {
			_action = { ..._action, [_key]: _value };
		} else {
			_params = { ..._params, [_key]: _value };
			_action = { ..._action, params: _params };
		}
	}

	return _action;
};

/**
 * normalize the returned actions with threshold values
 * to expected cloud format including params
 * {
 * 	index: n,
 * enabled: boolean,
 * urls: array,
 * params: {} //threshold values in object format
 * }
 * and skip action in removeActioons (external actions or roller for relay 25)
 */
const localActionsFormatToUi = (_actions, index = 0, removeActions = []) => {
	let actions = {};
	//if request returns error or for some reason it is falsy -> return alse;
	if (!_actions) return false;

	for (let [name, action] of Object.entries(_actions)) {
		if (!removeActions.includes(name)) {
			actions = { ...actions, [name]: normalizeLocalAction(action, index) };
		}
	}
	return actions;
};

/**
 * For multi channel devices in most cases we have
 *  - GENERIC device actions, which are available only for the channel : 0 by documentation
 *  - Sooo, we get the first channel data for those devices (always), and if the desired channel !== 0
 * 		we get the GENERIC actions (from channel one)
 * 		and override other actions by appropriate for the channel
 * and skip action in removeActions (external actions or roller for relay 25)
 */
const normalizeCloudMultiChannel = async (
	_actions: {} | boolean,
	device,
	url = 'undefined',
	removeActions = [],
) => {
	try {
		//in most cases for the first channel we don't need to remove anything
		// because this function for no wis used only for EM and RGBW2 white mode
		//but for sure pass the removeActions to the add index function
		// console.log("device ", device, _actions)
		if (device.channel === 0 || device.channel === '0')
			return addIndexToCloudActions(_actions, 0, removeActions);
		// console.log('here ', removeActions)
		let actions = addIndexToCloudActions(_actions, 0, removeActions) || {};
		let byDeviceChannel: GetCloudActionsRequest = await getCloudActions(
			real_id(device.id),
			device.channel,
			20000,
		); //postReq(url, {id: real_id(device.id), channel: device.channel});
		let channelActions = byDeviceChannel?.data?.actions || false;

		if (channelActions) {
			for (let [name, action] of Object.entries(channelActions)) {
				if (typeof action === 'object' && !removeActions.includes(name)) {
					let _action = { ...action, index: device.channel };
					//we ovveride the channel 0 values with actual value
					actions = { ...actions, [name]: _action };
				}
			}
			return actions;
		} else {
			return false;
		}
	} catch (error) {
		console.log(error);
		return _actions;
	}
};

/**
 * Do the same like for the cloud
 * if the current channel does not support generic actions, they are get from channel 0
 * and add the action_format params prop if need, which is missing from local response
 */
const normalizeLocalMultiChannel = (_actions, channel, removeActions) => {
	let actions = {};
	if (_actions) {
		for (let [name, action] of Object.entries(_actions)) {
			if (!removeActions.includes(name)) {
				//get generic action when action by channel not found
				let _action = action[channel]?.index === channel ? action[channel] : action[0];
				//extract threshold params such as cloud
				let _params = {};
				for (let [_key, _value] of Object.entries(_action)) {
					if (!['index', 'urls', 'enabled'].includes(_key)) {
						_params = { ..._params, [_key]: _value };
					}
				}
				if (Object.keys(_params).length !== 0) {
					_action['params'] = _params;
				}
				actions = { ...actions, [name]: _action };
			}
		}
		return actions;
	}
	return false;
};

/**
 * SHEM-3 still could not be implemented as all other devices, so
 * we handle it here
 * HINT: there is no need to remove any actions
 */
const getShem3Actions = async (device, localIp) => {
	try {
		const id = real_id(device.id);
		let res,
			_actions,
			actions = {};
		let url = localIp ? `${localIp}/settings/actions/` : `/device/get_actions_urls`;
		// console.log("LOCAL",localIp);
		//for (let i = 0; i < devices_config[device.type].modes[device.category].phases+1; i++) {//phases is prop inside devices_config for SHEM-3 only for now
		//for SHEM-3 it is always 3 phases, so don't need to check it
		for (let i = 0; i < 4; i++) {
			if (localIp) {
				//TODO need to review these local requested actions
				//here we get actions by phase index to handle local actions the same way as cloud actions
				res = await localReq(device, url, 'post', { index: i }, 2500, true);
				res = await getLocalActions(device, localIp, { index: i }, 2500, true);
				_actions = res?.actions || {};
			} else {
				//this channel isn't devices channel, because practically this device for now has only one channel this is phase
				res = await postReq(url, { id, channel: i });
				_actions = res?.data?.actions || {};
			}
			if (_actions) {
				for (let [name, action] of Object.entries(_actions)) {
					if (name.includes('power')) {
						let action_name = name; //here i am trying to override actions name for the sake of visualization. Basically 4th channel must be sum of all overpowered channels(phases)
						action_name = action_name + `_${i + 1}`;

						if (i === 3) {
							//like i said it is summing of all channels overpowered
							action_name = `sum_of_all_channels_${name.includes('under') ? 'under' : 'over'}_power_urls`;
						}

						// actions = { ...actions, ...{ [action_name]: action } };
						// console.log('actions 3em ', actions);
						// console.log('action', action);
						//add index for UI representation and then on SAVE
						action['index'] = i;
						if (localIp) {
							actions = {
								...actions,
								...{ [action_name]: normalizeLocalAction(action, 0) },
							};
						} else {
							actions = { ...actions, ...{ [action_name]: action } };
						}
					} else {
						if (i !== 0 && !name.includes('power')) continue; //here i am looking for first actions which is for output switch. Because this is one channeled for now it will support only one of a kind action for switch on and switch off
						action['index'] = 0;
						actions = {
							...actions,
							...{
								[name]: localIp ? normalizeLocalAction(action, 0) : action,
							},
						}; //and here i am overriding actions
					}
				}
			}
		}

		if (Object.values(actions).length > 0) return actions;
		return false;
	} catch (error) {
		return false;
	}
};

/**
 * GET only ADC actions for SHUNI-1
 */
export const getAdcActions = async (device, localIp) => {
	try {
		let actions, res, _actions;
		let id = real_id(device.id);
		if (localIp) {
			//get all actions for the device
			res = await getLocalActions(device, localIp, {});
			_actions = res?.actions || false;
			if (_actions) {
				for (let [name, action] of Object.entries(_actions)) {
					if (ADC_ACTIONS.includes(name)) {
						actions = {
							...actions,
							[name]: normalizeLocalAction(action, 0),
						};
					}
				}
			}
		} else {
			//always get channel 0 for adc;
			res = await getCloudActions(id, 0, 20000);
			_actions = res?.data?.actions || false;
			if (_actions) {
				for (let [name, action] of Object.entries(_actions)) {
					if (ADC_ACTIONS.includes(name) && typeof action !== 'boolean') {
						action['index'] = 0;
						actions = {
							...actions,
							[name]: action,
						};
					}
				}
			}
		}
		return actions;
	} catch (error) {
		return false;
	}
};

const getActionMultiChannel = async (device, localIp, removeActions) => {
	try {
		const id = real_id(device.id);
		let res, _actions;

		if (localIp) {
			res = await getLocalActions(device, localIp, {});
			_actions = res?.actions || false;
			return normalizeLocalMultiChannel(_actions, device.channel, removeActions);
		} else {
			res = await getCloudActions(id, 0, 20000);
			_actions = res?.data?.actions || false;
			//	console.log('PHP res actions ', _actions)
			return await normalizeCloudMultiChannel(_actions, device, 'undefined', removeActions);
		}
	} catch (error) {
		console.log('error on getting actions', error);
		return false;
	}
};

/**
 * Handle all sleeping devices,
 * all lights with one channel,
 * SHSW-21, SHSW-25 in relay mode and
 * I/O actions for SHUNI-1 (adc are different)
 */
const getActionsOneChannel = async (device, localIp, removeActions = []) => {
	try {
		let id = real_id(device.id);
		let res;
		/**
		 * for each returned action we check what we want to process
		 * all false values are sipped
		 * all actions in removeAction will not displayed,
		 * soo skip them in formating
		 */

		if (localIp && !SLEEPING_TYPES.includes(device.type)) {
			// res  = await localReq(device, url,'get', {id, index: device.channel});
			res = await getLocalActions(device, localIp, {});
			return localActionsFormatToUi(res?.actions || false, device.channel, removeActions);
		} else {
			res = await getCloudActions(id, device.channel, 20000);
			// res = await postReq(url, {id, channel: device.channel}, 250000);
			let _actions = res?.data?.actions || false;
			let actions = {};
			let params = {};
			if (_actions) {
				for (let [_n, _act] of Object.entries(_actions)) {
					if (typeof _act === 'boolean' || removeActions.includes(_n)) {
						continue;
					} else if (
						_n.includes('temp') &&
						!removeActions.includes(_n) &&
						HALF_SLEEPING_TYPES.includes(device.type)
					) {
						let action = {};
						params = _act;
						// actions = { ...actions, params: params };
						if (Object.keys(params).length !== 0) {
							Object.entries(params).map((key) => {
								if (key[0].includes('temp')) {
									action['params'] = { ...action['params'], [key[0]]: key[1] };
									delete _act[key[0]];
								}
							});
							let data = { ..._act };
							action = { ...action, ...data };
						}
						actions = { ...actions, [_n]: action };
					} else {
						_act['index'] = device.channel;
						actions = { ...actions, [_n]: _act };
					}
				}
				return actions;
			} else {
				console.error('HERE');
				return false;
			}
		}
	} catch (error) {
		// console.log("Error ", error);
		return false;
	}
};

export const getActions = async (device, localIp, removeActions = []) => {
	// console.log('device ', device, device.external_sensors_names);

	let _actions = {};
	if (device.type === 'SHEM-3') return await getShem3Actions(device, localIp);
	/** Because we have some devices which input count is the same as the device channel
	 * we don't need to ovverride anything, so we will get only one channel (the actual device.channel)
	 * so we use the get_action_one_channel
	 */
	if (
		device.channels_count === 1 ||
		['SHSW-21', 'SHSW-25', 'SHIX3-1', 'SHUNI-1'].includes(device.type)
	) {
		// console.log('Try to get one channel ', device)
		_actions = await getActionsOneChannel(device, localIp, removeActions);
	} else {
		// it is  for SHEM and SHRGBW2 white mode
		// console.log("get multi actions rgbw test")
		_actions = await getActionMultiChannel(device, localIp, removeActions);
	}
	return typeof _actions === 'object' && Object.keys(_actions).length > 0
		? _actions.hasOwnProperty('isok')
			? _actions
			: { isok: true, ..._actions }
		: false;
};

const normalize_local_external_sensors = (_actions, howExtTemp, isExtHum) => {
	let actions = {};
	if (_actions) {
		for (let [name, action] of Object.entries(_actions)) {
			// console.log(name, action);
			if (EXT_TEMP.includes(name) && typeof action !== 'boolean') {
				let t = action.reduce(function (prev, el) {
					let _el = {};
					let params = {};

					for (let [k, v] of Object.entries(el)) {
						if (['index', 'urls', 'enabled'].includes(k)) {
							_el = { ...el, [k]: v };
						} else {
							//adding temp sensor index to the params avoid
							// changes in Action's when we read params
							params = { ...params, [k + '_' + _el.index]: v };
							_el = { ...el, params };
						}
					}
					prev =
						_el.index < howExtTemp
							? { ...prev, [name + '_' + _el.index]: _el }
							: { ...prev };
					return prev;
				}, {});
				actions = { ...actions, ...t };
				// }
			} else if (isExtHum && EXT_HUM.includes(name) && typeof action !== 'boolean') {
				//if humidity or other action
				actions = { ...actions, [name]: normalizeLocalAction(action, 0) };
			} else {
				//skip all other actions
				continue;
			}
		}
	}
	console.log('Local external actions', actions);
	return Object.keys(actions).length === 0 ? false : actions;
};

const normalize_cloud_external_sensors = (_actions, i, isExtHum) => {
	let actions = {};
	for (let [_name, _action] of Object.entries(_actions)) {
		if (EXT_TEMP.includes(_name) && typeof _action === 'object') {
			_action['index'] = i;
			//rewrite params with index
			let _entries = _action.params;
			_action.params = {};
			for (let [_param, _value] of Object.entries(_entries)) {
				_action.params = { ..._action.params, [_param + '_' + i]: _value };
			}
			actions = { ...actions, [`${_name}_${i}`]: _action };
		} else if (isExtHum && EXT_HUM.includes(_name) && typeof _action === 'object') {
			_action['index'] = 0;
			actions = { ...actions, [_name]: _action };
		} else if (_name === 'report_url') {
			actions = { ...actions, [_name]: _action };
		} else {
			//all other will skipped
			continue;
		}
	}

	return actions;
};

export const get_external_sensor_actions = async (device, localIp, ext_temp, ext_hum) => {
	try {
		const id = real_id(device.id);
		let _actions,
			actions = {};
		let res;
		// console.log('external sensors set up ', ext_temp, ext_hum)
		let isExtHum = Object.keys(ext_hum).length > 0;
		let howExtTemp = Object.keys(ext_temp).length;

		if (localIp) {
			res = await getLocalActions(device, localIp, {});
			_actions = res?.actions || false;
			// console.log('LOCAL ext actions ', JSON.stringify(_actions))
			//here we get all external sensors actions
			actions = normalize_local_external_sensors(_actions, howExtTemp, isExtHum);
			// console.log('AFTER NEW FUNCT ', actions);
		} else {
			//here we get each external sensor with single request
			// in response we have all devices actions, so we filter them
			for (let i = 0; i < howExtTemp; i++) {
				res = await getCloudActions(id, i, 20000);
				_actions = res?.data?.actions || {};
				actions = {
					...actions,
					...normalize_cloud_external_sensors(_actions, i, isExtHum),
				};
			}
			// console.log("CLOUD actions after new function", JSON.stringify(actions));
		}
		return Object.keys(actions).length > 0 ? { isok: true, ...actions } : false;
	} catch (error) {
		return false;
	}
};

const prepare_action_details = (_action, _params, _action_index, type) => {
	let action = _action,
		params = { ..._params };

	if (action.match(/ext_temp/)) {
		action = _action.split(`_${_action_index}`)[0];
		params = {};
		for (let [_k, _v] of Object.entries(_params)) {
			params = { ...params, [_k.split(`_${_action_index}`)[0]]: _v };
		}
	}

	if (type === 'SHEM-3') {
		if (_action.match(/\d/)) {
			action = action.split('_').slice(0, -1).join('_'); //now removing number from name
		} else if (!action?.match(/\d/) && action?.includes('power')) {
			action = `${_action?.includes('under') ? 'under' : 'over'}_power_url`;
		}
	}

	return { action, params };
};

export const save_actions = async (device, checked, _action, list, _params, actionIndex, local_ip) => {
	let res, act, channel;
	let { action, params } = prepare_action_details(_action, _params, actionIndex, device.type);

	const id = real_id(device.id);

	list.forEach((el) => {
		if (el?.url) {
			el.url = encodeURIComponent(el.url);
		} else if (el) {
			el = encodeURIComponent(el);
		}
	});

	let data = {
		id: id,
		channel: actionIndex, //device.channel === actionIndex ? device.channel : actionIndex,
		enabled: checked && list.length > 0 ? true : checked === 1 ? true : false,
		type: action,
		urls: list.length > 0 && typeof list[0] !== 'undefined' ? list : [''],
		params: JSON.stringify(params),
	};

	if (local_ip && !SLEEPING_TYPES.includes(device.type)) {
		let URL = local_ip + '/settings/actions/?';
		let _index = actionIndex;
		// device.type === 'SHEM-3' && channel
		// 	? channel - 1
		// 	: device.channel === actionIndex
		// 	? device.channel
		// 	: actionIndex;
		let _enabled = checked && list.length > 0 ? true : checked === 1 ? true : false;
		let _params = '';

		//handle threshold for power urls and additional params such as one time or multiple (adc actions)
		if (params !== undefined) {
			for (let [name, value] of Object.entries(params)) {
				if (_params === '') {
					_params = `${name}=${value}`;
				} else {
					_params += `&${name}=${value}`;
				}
			}
		}
		let _name = act && params !== undefined ? act : action;
		let urls = `index=${_index}&enabled=${_enabled}&name=${_name}&${_params}`;

		list.forEach((url) => {
			urls += `&urls[]=${encodeURIComponent(url)}`;
		});

		if (list.length === 0) {
			urls += `&urls[]`;
		}

		res = await localReq(device, URL + urls, 'post', {});
	} else {
		res = await postReq(`/device/settings/actions_urls`, data, 20000);
	}

	return res;
};
